import React, { useEffect, useState, useCallback } from 'react';
import { authAPI, adminAPI } from '../utils/authUtils';
import { Navigate, useNavigate } from 'react-router-dom';
import { scrollToForm } from '../utils/adminFormUtils';
import { handleError } from '../utils/errorHandler';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorDisplay from '../components/ErrorDisplay';
import '../styles/adminCommonStyles.css';

// Password complexity validation
const validatePassword = (password) => {
  const requirements = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };
  
  const isValid = Object.values(requirements).every(req => req);
  return { isValid, requirements };
};

// Generate complex password
const generateComplexPassword = () => {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const specialChars = '!@#$%^&*()_+-=[]{};\':"|,.<>?/';
  
  let password = '';
  password += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
  password += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
  password += numbers.charAt(Math.floor(Math.random() * numbers.length));
  password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));
  
  const allChars = uppercase + lowercase + numbers + specialChars;
  for (let i = password.length; i < 12; i++) {
    password += allChars.charAt(Math.floor(Math.random() * allChars.length));
  }
  
  // Shuffle password
  password = password.split('').sort(() => Math.random() - 0.5).join('');
  
  return password;
};

export default function AdminUsers() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ username: '', password: '', role: 'admin' });
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addPasswordValidation, setAddPasswordValidation] = useState(null);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', username: '', password: '', role: 'admin' });
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editPasswordValidation, setEditPasswordValidation] = useState(null);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  // Check user role on mount
  useEffect(() => {
    setIsSuperAdmin(authAPI.hasRole('superadmin'));
  }, []);

  // Fetch admins
  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getAdmins();
      const { normalizeList } = await import('../utils/responseHelpers');
      setAdmins(normalizeList(response));
    } catch (err) {
      setError(handleError(err, 'Failed to fetch admins'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  // Success message helper
  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Generate password handler
  const handleGeneratePassword = () => {
    const newPassword = generateComplexPassword();
    setAddForm({ ...addForm, password: newPassword });
    setAddPasswordValidation(validatePassword(newPassword));
  };

  // Add admin handlers
  const handleAddInput = (e) => {
    const { name, value } = e.target;
    setAddForm({ ...addForm, [name]: value });
    
    // Validate password on change
    if (name === 'password') {
      setAddPasswordValidation(validatePassword(value));
    }
  };
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);
    
    // Validate password complexity
    const passwordValidation = validatePassword(addForm.password);
    if (!passwordValidation.isValid) {
      setAddError('Password must be at least 8 characters and contain uppercase, lowercase, number, and special character.');
      setAddLoading(false);
      return;
    }
    
    try {
      await adminAPI.createAdmin(addForm);
      setShowAddModal(false);
  setAddForm({ username: '', password: '', role: 'admin' });
  setAddPasswordValidation(null);
      showSuccess('Admin added successfully!');
      fetchAdmins();
    } catch (err) {
      setAddError(handleError(err, 'Failed to add admin'));
    } finally {
      setAddLoading(false);
    }
  };

  // Edit admin handlers
  const handleEditClick = (admin) => {
    setEditForm({ id: admin.id, username: admin.username, password: '', role: admin.role });
    setShowEditModal(true);
    setEditError(null);
    setEditPasswordValidation(null);
  };
  const handleEditInput = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
    
    // Validate password on change (only if password field has value)
    if (name === 'password' && value) {
      setEditPasswordValidation(validatePassword(value));
    } else if (name === 'password' && !value) {
      setEditPasswordValidation(null);
    }
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);
    
    // Validate password complexity if password is being changed
    if (editForm.password) {
      const passwordValidation = validatePassword(editForm.password);
      if (!passwordValidation.isValid) {
        setEditError('Password must be at least 8 characters and contain uppercase, lowercase, number, and special character.');
        setEditLoading(false);
        return;
      }
    }
    
    try {
      const { id, ...updateData } = editForm;
      await adminAPI.updateAdmin(id, updateData);
      setShowEditModal(false);
      setEditPasswordValidation(null);
      showSuccess('Admin updated successfully!');
      fetchAdmins();
    } catch (err) {
      setEditError(handleError(err, 'Failed to update admin'));
    } finally {
      setEditLoading(false);
    }
  };

  // Promote admin to superadmin
  const handlePromoteAdmin = async (adminId) => {
    try {
      await adminAPI.promoteToSuperAdmin(adminId);
      showSuccess('Admin promoted to superadmin!');
      fetchAdmins();
    } catch (err) {
      setError(handleError(err, 'Failed to promote admin'));
    }
  };

  // Delete admin
  const handleDeleteAdmin = async (adminId, adminEmail) => {
    if (!isSuperAdmin) {
      alert('Access denied. Only superadmin can delete admins.');
      return;
    }
    const confirmDelete = window.confirm(`Are you sure you want to delete "${adminEmail}"? This action cannot be undone.`);
    if (!confirmDelete) return;
    setDeleteLoading(adminId);
    setError(null);
    try {
      await adminAPI.deleteAdmin(adminId);
      setAdmins(prev => prev.filter(a => a.id !== adminId));
      showSuccess('Admin deleted successfully!');
    } catch (err) {
      setError(handleError(err, 'Failed to delete admin'));
    } finally {
      setDeleteLoading(null);
    }
  };

  // Logout
  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold mb-4 text-red-600">Access Denied</h2>
        <p className="text-gray-600">Only superadmins can view this page.</p>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 md:p-8 w-full max-w-screen-2xl mx-auto flex flex-col items-center sm:items-start justify-center sm:justify-start">
      <div className="w-full flex flex-col items-center sm:items-start gap-4 sm:flex-row sm:justify-between mb-6">
        <h2 className="text-3xl md:text-4xl font-bold text-black mb-2 text-center sm:text-left">Admin Users</h2>
        <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${isSuperAdmin ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`}>
            {isSuperAdmin ? 'Super Admin' : 'Admin'}
          </span>
          <button
            onClick={handleLogout}
            className="admin-btn-danger"
          >
            Logout
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="admin-alert-success">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="admin-alert-error">
          <ErrorDisplay error={error} />
        </div>
      )}

      <div className="flex justify-center w-full">
        <button
          className="admin-btn-primary-lg mb-6"
          style={{ width: 'auto', display: 'inline-block' }}
          onClick={() => {
            setShowAddModal(true);
            setTimeout(() => {
              scrollToForm();
            }, 0);
          }}
        >
          + Add New Admin
        </button>
      </div>

      {showAddModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content admin-modal-form" id="add-user-form">
            <h3 className="admin-modal-title">Add New Admin</h3>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-4">
                <label className="admin-label">Username</label>
                <input
                  type="text"
                  name="username"
                  value={addForm.username}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Password</label>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1 relative">
                    <input
                      type={showAddPassword ? "text" : "password"}
                      name="password"
                      value={addForm.password}
                      onChange={handleAddInput}
                      className="admin-input pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowAddPassword(!showAddPassword)}
                      className="absolute right-3 top-2.5 text-gray-600 hover:text-gray-900 transition-colors"
                      title={showAddPassword ? "Hide password" : "Show password"}
                    >
                      {showAddPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="admin-btn-success"
                  >
                    Generate
                  </button>
                </div>
                {addPasswordValidation && (
                  <div className="mt-3 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
                    <ul className="text-xs space-y-1">
                      <li className={addPasswordValidation.requirements.minLength ? 'text-green-700' : 'text-red-700'}>
                        {addPasswordValidation.requirements.minLength ? '✓' : '✗'} At least 8 characters
                      </li>
                      <li className={addPasswordValidation.requirements.hasUpperCase ? 'text-green-700' : 'text-red-700'}>
                        {addPasswordValidation.requirements.hasUpperCase ? '✓' : '✗'} Uppercase letter (A-Z)
                      </li>
                      <li className={addPasswordValidation.requirements.hasLowerCase ? 'text-green-700' : 'text-red-700'}>
                        {addPasswordValidation.requirements.hasLowerCase ? '✓' : '✗'} Lowercase letter (a-z)
                      </li>
                      <li className={addPasswordValidation.requirements.hasNumber ? 'text-green-700' : 'text-red-700'}>
                        {addPasswordValidation.requirements.hasNumber ? '✓' : '✗'} Number (0-9)
                      </li>
                      <li className={addPasswordValidation.requirements.hasSpecialChar ? 'text-green-700' : 'text-red-700'}>
                        {addPasswordValidation.requirements.hasSpecialChar ? '✓' : '✗'} Special character (!@#$%^&* etc.)
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="admin-label">Role</label>
                <select
                  name="role"
                  value={addForm.role}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
              {addError && (
                <div className="admin-alert-error-inline">
                  {addError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary w-fit"
                  style={{maxWidth: '140px'}}
                  onClick={() => {
                    setShowAddModal(false);
                    setAddError(null);
                  }}
                  disabled={addLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary w-fit"
                  style={{maxWidth: '140px'}}
                  disabled={addLoading}
                >
                  {addLoading ? 'Adding...' : 'Add Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="admin-modal-overlay-scroll">
          <div className="admin-modal-content">
            <h3 className="admin-modal-title">Edit Admin</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-4">
                <label className="admin-label">Username</label>
                <input
                  type="text"
                  name="username"
                  value={editForm.username}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Password (leave blank to keep unchanged)</label>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1 relative">
                    <input
                      type={showEditPassword ? "text" : "password"}
                      name="password"
                      value={editForm.password}
                      onChange={handleEditInput}
                      className="admin-input pr-10"
                    />
                    {editForm.password && (
                      <button
                        type="button"
                        onClick={() => setShowEditPassword(!showEditPassword)}
                        className="absolute right-3 top-2.5 text-gray-600 hover:text-gray-900 transition-colors"
                        title={showEditPassword ? "Hide password" : "Show password"}
                      >
                        {showEditPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                {editForm.password && editPasswordValidation && (
                  <div className="mt-3 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
                    <ul className="text-xs space-y-1">
                      <li className={editPasswordValidation.requirements.minLength ? 'text-green-700' : 'text-red-700'}>
                        {editPasswordValidation.requirements.minLength ? '✓' : '✗'} At least 8 characters
                      </li>
                      <li className={editPasswordValidation.requirements.hasUpperCase ? 'text-green-700' : 'text-red-700'}>
                        {editPasswordValidation.requirements.hasUpperCase ? '✓' : '✗'} Uppercase letter (A-Z)
                      </li>
                      <li className={editPasswordValidation.requirements.hasLowerCase ? 'text-green-700' : 'text-red-700'}>
                        {editPasswordValidation.requirements.hasLowerCase ? '✓' : '✗'} Lowercase letter (a-z)
                      </li>
                      <li className={editPasswordValidation.requirements.hasNumber ? 'text-green-700' : 'text-red-700'}>
                        {editPasswordValidation.requirements.hasNumber ? '✓' : '✗'} Number (0-9)
                      </li>
                      <li className={editPasswordValidation.requirements.hasSpecialChar ? 'text-green-700' : 'text-red-700'}>
                        {editPasswordValidation.requirements.hasSpecialChar ? '✓' : '✗'} Special character (!@#$%^&* etc.)
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="admin-label">Role</label>
                <select
                  name="role"
                  value={editForm.role}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
              {editError && (
                <div className="admin-alert-error-inline">
                  {editError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="admin-btn-secondary w-fit"
                  style={{maxWidth: '140px'}}
                  onClick={() => {
                    setShowEditModal(false);
                    setEditError(null);
                  }}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-success w-fit"
                  style={{maxWidth: '140px'}}
                  disabled={editLoading}
                >
                  {editLoading ? 'Updating...' : 'Update Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading && (
        <LoadingSpinner message="Loading admins..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
      )}

      {!loading && (
        <div className="admin-table-container">
          <div className="overflow-x-auto">
            <table className="admin-table"
              style={{borderSpacing: 0, borderCollapse: 'separate'}}>
              <thead className="admin-table-head">
                <tr>
                  <th className="admin-table-header-cell">Username</th>
                  <th className="admin-table-header-cell">Role</th>
                  <th className="admin-table-header-cell" style={{width: '120px'}}>Actions</th>
                </tr>
              </thead>
              <tbody className="admin-table-body-row">
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="py-8 px-4 text-center sm:text-left text-gray-500">
                      <div className="text-lg">No admins found</div>
                      <div className="text-sm mt-1">Add your first admin to get started!</div>
                    </td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-blue-50 transition-colors text-center sm:text-left">
                      <td className="py-2 px-2 font-medium text-gray-900 text-center sm:text-left">{admin.username}</td>
                      <td className="py-2 px-2 text-center sm:text-left whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-semibold break-words sm:break-normal ${admin.role === 'superadmin' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-blue-100 text-blue-800 border border-blue-300'}`} style={{wordBreak: 'break-word', lineHeight: '1.2'}}>
                          {admin.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-center sm:text-left" style={{width: '120px'}}>
                        <div className="flex flex-col gap-1 items-center sm:items-stretch justify-center sm:justify-start">
                          <button
                            onClick={() => handleEditClick(admin)}
                            className="admin-btn-table-edit"
                          >
                            Edit
                          </button>
                          {admin.role !== 'superadmin' && (
                            <button
                              onClick={() => handlePromoteAdmin(admin.id)}
                              className="admin-btn-table-success"
                            >
                              Promote to Superadmin
                            </button>
                          )}
                          {isSuperAdmin ? (
                            <button
                            onClick={() => handleDeleteAdmin(admin.id, admin.username)}
                              disabled={deleteLoading === admin.id}
                              className="admin-btn-table-danger"
                            >
                              {deleteLoading === admin.id ? 'Deleting...' : 'Delete'}
                            </button>
                          ) : (
                            <button
                              disabled
                              className="admin-btn-table-disabled"
                              title="Superadmin role required to delete admins"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}